import {
  Briefcase,
  DollarSign,
  Eye,
  Trash,
  Upload,
} from "lucide-react";
import { EditDatePicker } from "@/components/ui/EditDatePicker";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { NinjaIframePopup } from "@/components/bank/NinjaIframePopup";
import { linkNinjaAccount } from "@/lib/api/bankService";
import { showSuccess, showError } from "@/lib/utils/notifications";
import { useGoogleTranslateRefresh } from "@/hooks/useGoogleTranslateRefresh";
import { IncomeDocumentationSection } from "@/features/applicant/components/IncomeDocumentationSection";
import styles from "../../../styles/applicationDetail.module.css";
import editStyles from "../../../styles/editForm.module.css";
import {
  INCOME_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL,
  OTHER_INCOME_PERIODS,
} from "../../../constants";
import {
  buildApplicantDisplayName,
  calculateYearlyIncome,
  calculateJobDuration,
  formatIncomeAttachmentUploadMeta,
  showSupportingUploadsSection,
} from "./incomeEmploymentHelpers";
import { EmploymentEditForm } from "./EmploymentEditForm";
import { JobEndDatePopup } from "./JobEndDatePopup";
import { BankLinkingEditSection } from "../BankLinkingSection";
import type { IncomeEmploymentEditViewProps } from "../../../types/incomeEmployment";
import type { IncomeAttachmentFile, OtherIncomeFormRow } from "../../../types/incomeEmploymentApi";

const OTHER_INCOME_FIELD_BASE =
  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500";
const OTHER_INCOME_SELECT_BASE =
  "w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500";

function otherIncomeInputClass(hasError: boolean, base: string): string {
  return hasError ? `${base} ${editStyles.inputError}` : base;
}

export function IncomeEmploymentEditView({
  applicantId,
  applicationData,
  onRefresh,
  employment,
  documents,
  bankLinking,
  incomeVerificationDocuments,
  handleApplicationIncomeFilePreview,
}: IncomeEmploymentEditViewProps) {
  const {
    currentJobForms,
    previousJobForms,
    otherIncomeForms,
    employmentStatus,
    hasPreviousEmployer,
    submitAttempted,
    statesList,
    propertyAddressForDistance,
    pendingMakePreviousIndex,
    setPendingMakePreviousIndex,
    pendingMakePreviousEndDate,
    setPendingMakePreviousEndDate,
    makePreviousConfirm,
    setMakePreviousConfirm,
    makeCurrentConfirm,
    setMakeCurrentConfirm,
    handleOtherIncomeChange,
    handleAddOtherIncome,
    handleRemoveOtherIncome,
    handleCurrentJobChange,
    handleCurrentJobTranslate,
    handlePreviousJobTranslate,
    handleCurrentJobBatchChange,
    handlePreviousJobChange,
    handlePreviousJobBatchChange,
    handleAddCurrentJob,
    handleRemoveCurrentJob,
    handleAddPreviousJob,
    handleRemovePreviousJob,
    openMakePreviousPopup,
    handleMakePrevious,
    handleMakeCurrent,
    handleEmploymentStatusChange,
    handlePreviousEmployerToggle,
    handleCurrentJobAddressBlur,
    handleCurrentJobAddressResolved,
    otherIncomeFieldMsg,
  } = employment;

  const {
    proofOfIncomeUploading,
    handleIncomeDocumentFiles,
    handleRemoveProofOfIncome,
    ninjaIncomeDocuments,
    proofIncomeDocuments,
    supportingIncomeUploadMode,
    storedNinjaIncomeProofCount,
    storedProofOfIncomeCount,
  } = documents;

  const supportingUploadExistingCount =
    supportingIncomeUploadMode === "resident"
      ? storedProofOfIncomeCount
      : storedNinjaIncomeProofCount;
  const supportingUploadExistingNames =
    supportingIncomeUploadMode === "resident"
      ? proofIncomeDocuments.map((f) => f.filename || f.name || "")
      : ninjaIncomeDocuments.map((f) => f.filename || f.name || "");

  const applicantName = buildApplicantDisplayName(applicationData);
  const isAwaitingStatus = applicationData?.applicant_status === "AWAITING";

  const renderEditSupportingFileRow = (
    file: IncomeAttachmentFile,
    index: number,
    options: {
      allFiles: IncomeAttachmentFile[];
      allowRemove?: boolean;
    },
  ) => {
    const { displayName, uploadedMetaText } = formatIncomeAttachmentUploadMeta(
      file,
      applicantName,
    );

    return (
      <div
        key={file.attachment_id || index}
        className="flex flex-col p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-[15px] font-medium text-[#232946] break-all">
            {displayName}
          </span>

          {(file.filepath || file.attachment_id) && (
            <button
              type="button"
              onClick={() => {
                void handleApplicationIncomeFilePreview(file, options.allFiles);
              }}
              className="p-1 text-purple-600 hover:bg-purple-50 rounded flex-shrink-0"
              title="View"
              aria-label={`View ${displayName}`}
            >
              <Eye className="h-[18px] w-[18px]" />
            </button>
          )}
          {options.allowRemove && file.attachment_id != null && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void handleRemoveProofOfIncome(file.attachment_id!);
              }}
              className="p-1 text-red-600 hover:bg-red-50 rounded flex-shrink-0"
              title="Delete"
              aria-label={`Delete ${displayName}`}
            >
              <Trash className="h-[18px] w-[18px]" />
            </button>
          )}
        </div>
        {uploadedMetaText && (
          <span className="text-[14px] text-gray-500 mt-2">{uploadedMetaText}</span>
        )}
      </div>
    );
  };

  const {
    ninjaAccounts,
    ninjaConfig,
    isBankLinkingLoading,
    showNinjaIframePopup,
    setShowNinjaIframePopup,
    ninjaIbvResponseData,
    setNinjaIbvResponseData,
    isOpeningNinjaBankLink,
    handleLinkBank,
    refreshAccounts,
  } = bankLinking;

  // Other Income rows are mounted inline on "Add Other Income"; re-run Google Translate
  // so Spanish applies to the freshly mounted fields (Angular keeps them in-DOM).
  useGoogleTranslateRefresh(otherIncomeForms.length);

  return (
      <div
        className={`${styles.tabContent} ${styles.incomeEmploymentPremium} ${styles.incomeEmploymentEditPremium}`}
        data-testid="application-income-tab-edit-page"
      >
        {/* Header and Employment Status */}
        <div className={styles.section} data-testid="application-income-tab-section-income-and-employment-provide-2-years-of">
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <DollarSign className="h-4 w-4" />
            </span>
            Income and Employment* (Provide 2 years of employment and other
            income history)
          </h2>
          <div className={styles.incomeEditNote}>
            WARNING: If you do not provide at least two years of work history, Liberty Rent will request additional information, which could delay the application process.
          </div>

          {/* Employment Status Radio Buttons */}

          {/* Employment Status Radio Buttons */}
          <div className={styles.employmentCurrentContainer}>
          <h3 className={styles.incomeSubsectionTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <Briefcase className="h-4 w-4" />
            </span>
            Current Employment
          </h3>
          <div className={editStyles.formRow}>
            {/* ... Radio buttons implementation remains ... */}
            <div className={`${editStyles.formGroupFull} ${styles.employmentStatusBlock}`}>
              <label className={editStyles.label} data-testid="application-income-tab-employment-status-label">
                <strong>Employment Status*</strong>
              </label>
              <div className={editStyles.radioGroup}>
                <label className={editStyles.radioLabel} data-testid="application-income-tab-employment-status-employed-label">
                  <input
                    type="radio"
                    name="employment_status"
                    value="1"
                    checked={employmentStatus === "1"}
                    data-testid="application-income-tab-employment-status-employed-input"
                onChange={() => handleEmploymentStatusChange("1")}
                  />
                  <span>Employed</span>
                </label>
                <label className={editStyles.radioLabel} data-testid="application-income-tab-employment-status-not-employed-label">
                  <input
                    type="radio"
                    name="employment_status"
                    value="0"
                    checked={employmentStatus === "0"}
                    data-testid="application-income-tab-employment-status-not-employed-input"
                onChange={() => handleEmploymentStatusChange("0")}
                  />
                  <span>Not Employed</span>
                </label>
                {employmentStatus !== "0" && employmentStatus !== "1" && (
                  <span
                    className={styles.employmentStatusSelectValue}
                    data-testid="application-income-tab-employment-status-select-value"
                  >
                    * Select value
                  </span>
                )}
              </div>
              {/* Angular shows this Note unconditionally next to the Employment Status radios. */}
              <p className={styles.incomeEditNote}>
                (Note: if &lsquo;Not Employed&rsquo; is selected please provide
                Other Sources of Income below)
              </p>
            </div>

          </div>

          {/* Current Employer - Edit Mode (only show if employed) */}
          {employmentStatus === "1" && (
          <div className={styles.incomeNestedSection} data-testid="application-income-tab-section-current-employer-list-all-jobs-where-you">
            <p className={styles.incomeSubsectionDescription}>
              List all jobs where you are currently employed or formal offers
              that you plan to accept.
            </p>
            {currentJobForms.map((row, index) => (
              <div key={`current-${index}`} className="mb-4">
                <EmploymentEditForm
                  formData={row}
                  onChange={(field, value) =>
                    handleCurrentJobChange(index, field, value)
                  }
                  onBatchChange={(updates) =>
                    handleCurrentJobBatchChange(index, updates)
                  }
                  employmentType="current_job"
                  onAddressBlur={() => handleCurrentJobAddressBlur(index)}
                  onTranslateBlur={(field, value) =>
                    handleCurrentJobTranslate(index, field, value)
                  }
                  onAddressResolved={(updates) =>
                    handleCurrentJobAddressResolved(index, updates)
                  }
                  propertyAddress={propertyAddressForDistance}
                  statesList={statesList}
                  showRemove={!!row.job_id || currentJobForms.length > 1}
                  onRemove={() => handleRemoveCurrentJob(index)}
                  onMakeOther={
                    row.job_id
                      ? () => openMakePreviousPopup(index)
                      : undefined
                  }
                  makeOtherLabel="Make Previous Job"
                  rowIndex={index}
                  submitAttempted={submitAttempted}
                />
              </div>
            ))}
            <button
              type="button"
              className="flex items-center text-blue-500 hover:text-blue-600 font-medium mt-2"
              data-testid="application-income-tab-add-current-job-button"
                onClick={handleAddCurrentJob}
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Other Current Employers (i.e. multiple part-time jobs)
            </button>
          </div>
          )}
          </div>

        {/* Previous Employer - Edit Mode */}
        <div className={styles.incomeNestedSection} data-testid="application-income-tab-edit-section-previous-employer">
          <h3 className={styles.incomeSubsectionTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <Briefcase className="h-4 w-4" />
            </span>
            Previous Employer
          </h3>
          <div className={`${editStyles.formRow} ${styles.previousEmployerStatusBlock}`}>
            <div className={editStyles.radioGroup}>
              <label className={editStyles.radioLabel} data-testid="application-income-tab-has-previous-employer-yes-label">
                <input
                  type="radio"
                  name="has_previous_employer"
                  checked={hasPreviousEmployer}
                  data-testid="application-income-tab-has-previous-employer-yes-input"
                onChange={() => handlePreviousEmployerToggle(true)}
                />
                <span>I Have A Previous Employer</span>
              </label>
              <label className={editStyles.radioLabel} data-testid="application-income-tab-has-previous-employer-no-label">
                <input
                  type="radio"
                  name="has_previous_employer"
                  checked={!hasPreviousEmployer}
                  data-testid="application-income-tab-has-previous-employer-no-input"
                onChange={() => handlePreviousEmployerToggle(false)}
                />
                <span>I Have No Previous Employer</span>
              </label>
            </div>
          </div>

          {hasPreviousEmployer && (
            <>
              {previousJobForms.map((row, index) => (
                <div key={`previous-${index}`} className="mb-4">
                  <EmploymentEditForm
                    formData={row}
                    onChange={(field, value) =>
                      handlePreviousJobChange(index, field, value)
                    }
                    onBatchChange={(updates) =>
                      handlePreviousJobBatchChange(index, updates)
                    }
                    employmentType="previous_job"
                    onTranslateBlur={(field, value) =>
                      handlePreviousJobTranslate(index, field, value)
                    }
                    statesList={statesList}
                    showRemove={!!row.job_id || previousJobForms.length > 1}
                    onRemove={() => handleRemovePreviousJob(index)}
                    onMakeOther={
                      row.job_id
                        ? () => setMakeCurrentConfirm({ rowIndex: index })
                        : undefined
                    }
                    makeOtherLabel="Make Current Job"
                    rowIndex={index}
                    submitAttempted={submitAttempted}
                  />
                </div>
              ))}
              <button
                type="button"
                className="flex items-center text-blue-500 hover:text-blue-600 font-medium mt-2"
                data-testid="application-income-tab-add-previous-job-button"
                onClick={handleAddPreviousJob}
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Previous Employer
              </button>
            </>
          )}
        </div>

        {/* Other Income Sources - Editable */}
        <div className={styles.incomeNestedSection} data-testid="application-income-tab-section-other-sources-of-income">
          <h3 className={styles.incomeSubsectionTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <DollarSign className="h-4 w-4" />
            </span>
            Other Sources of Income
          </h3>

          {/* Existing Other Income Sources */}
          {otherIncomeForms.map((incomeForm: OtherIncomeFormRow, index: number) => (
            <div
              key={index}
              className={styles.incomeOtherSourceCard}
            >
              {/* Remove Button */}
              <div className={styles.incomeOtherSourceCardHeader}>
                <span>Other Income {index + 1}</span>
                <button
                  type="button"
                  className={styles.incomeRemoveButton}
                  data-testid={`application-income-tab-other-income-${index}-remove-button`}
                  onClick={() => handleRemoveOtherIncome(index)}
                >
                  <Trash className="h-3.5 w-3.5" aria-hidden />
                  Remove
                </button>
              </div>

              <div className="grid grid-cols-12 gap-6">
                {/* Row 1: Frequency, Source, Amount, Date/Yearly */}

                {/* 1. Frequency (3 cols) */}
                <div className="col-span-12 md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={`application-income-tab-other-income-${index}-income-frequency-label`}>
                    Income Frequency<span className="text-red-500">*</span>
                  </label>
                  <select
                    className={otherIncomeInputClass(
                      Boolean(otherIncomeFieldMsg(index, "income_period")),
                      OTHER_INCOME_SELECT_BASE,
                    )}
                    value={incomeForm.income_period}
                    data-testid={`application-income-tab-other-income-${index}-income-frequency-select`}
                onChange={(e) =>
                      handleOtherIncomeChange(
                        index,
                        "income_period",
                        e.target.value,
                      )
                    }
                  >
                    <option value="">Choose Frequency...</option>
                    {Object.entries(OTHER_INCOME_PERIODS).map(
                      ([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ),
                    )}
                  </select>
                  {otherIncomeFieldMsg(index, "income_period") ? (
                    <div className={editStyles.errorMessage} role="alert">
                      {otherIncomeFieldMsg(index, "income_period")}
                    </div>
                  ) : null}
                </div>

                {/* 2. Source Name (3 cols) */}
                <div className="col-span-12 md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={`application-income-tab-other-income-${index}-source-name-label`}>
                    Source Name<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={otherIncomeInputClass(
                      Boolean(otherIncomeFieldMsg(index, "source_name")),
                      OTHER_INCOME_FIELD_BASE,
                    )}
                    value={incomeForm.source_name}
                    data-testid={`application-income-tab-other-income-${index}-source-name-input`}
                onChange={(e) =>
                      handleOtherIncomeChange(
                        index,
                        "source_name",
                        e.target.value,
                      )
                    }
                    placeholder="Source Name"
                  />
                  {otherIncomeFieldMsg(index, "source_name") ? (
                    <div className={editStyles.errorMessage} role="alert">
                      {otherIncomeFieldMsg(index, "source_name")}
                    </div>
                  ) : null}
                </div>

                {/* 3. Income / Amount (3 cols) — Angular `appAmount` auto-$ + digit cap. */}
                <div className="col-span-12 md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={`application-income-tab-other-income-${index}-income-amount-label`}>
                    {incomeForm.income_period === "One_Time"
                      ? "Amount"
                      : "Income"}
                    <span className="text-red-500">*</span>
                  </label>
                  <CurrencyInput
                    value={incomeForm.income != null ? String(incomeForm.income) : ""}
                    data-testid={`application-income-tab-other-income-${index}-income-amount-input`}
                    onChange={(value) =>
                      handleOtherIncomeChange(index, "income", value)
                    }
                    placeholder="$0.00"
                    maxDigitsBeforeDecimal={INCOME_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL}
                    className={
                      otherIncomeFieldMsg(index, "income")
                        ? editStyles.inputError
                        : undefined
                    }
                  />
                  {otherIncomeFieldMsg(index, "income") ? (
                    <div className={editStyles.errorMessage} role="alert">
                      {otherIncomeFieldMsg(index, "income")}
                    </div>
                  ) : null}
                </div>

                {/* 4. Transaction Date OR Yearly Income (3 cols) */}
                <div className="col-span-12 md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={`application-income-tab-other-income-${index}-transaction-or-yearly-label`}>
                    {incomeForm.income_period === "One_Time"
                      ? "Transaction Date"
                      : "Yearly Calculated Income"}
                    {incomeForm.income_period === "One_Time" && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>

                  {incomeForm.income_period === "One_Time" ? (
                    <div className="w-full">
                      <EditDatePicker
                        value={incomeForm.job_start_date ?? ''}
                        onChange={(value) =>
                          handleOtherIncomeChange(index, "job_start_date", value)
                        }
                        allowPastDates={true}
                        allowFutureDates={false}
                        displayFormat="mdy"
                        calendarPortalId="liberty-income-employment-datepicker"
                        className={
                          otherIncomeFieldMsg(index, "job_start_date")
                            ? editStyles.inputError
                            : ""
                        }
                      />
                      {otherIncomeFieldMsg(index, "job_start_date") ? (
                        <div className={editStyles.errorMessage} role="alert">
                          {otherIncomeFieldMsg(index, "job_start_date")}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-600 cursor-not-allowed"
                      value={calculateYearlyIncome(
                        incomeForm.income ?? '',
                        incomeForm.income_period ?? '',
                      )}
                      readOnly
                      data-testid={`application-income-tab-other-income-${index}-yearly-calculated-income-input`}
                    />
                  )}
                </div>

                {/* Row 2: Recurring Income Details (Start, End, Active, Duration) */}
                {incomeForm.income_period !== "One_Time" && (
                  <>
                    <div className="col-span-12 md:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={`application-income-tab-other-income-${index}-start-date-label`}>
                        Start Date<span className="text-red-500">*</span>
                      </label>
                      <div className="w-full">
                        <EditDatePicker
                          value={incomeForm.job_start_date ?? ''}
                          onChange={(value) =>
                            handleOtherIncomeChange(index, "job_start_date", value)
                          }
                          allowPastDates={true}
                          allowFutureDates={false}
                          displayFormat="mdy"
                          calendarPortalId="liberty-income-employment-datepicker"
                          className={
                            otherIncomeFieldMsg(index, "job_start_date")
                              ? editStyles.inputError
                              : ""
                          }
                        />
                        {otherIncomeFieldMsg(index, "job_start_date") ? (
                          <div className={editStyles.errorMessage} role="alert">
                            {otherIncomeFieldMsg(index, "job_start_date")}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="col-span-12 md:col-span-3 flex items-end gap-2">
                      <div className="flex-grow">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={`application-income-tab-other-income-${index}-end-date-label`}>
                          End Date
                          {!incomeForm.present_job ? (
                            <span className="text-red-500">*</span>
                          ) : null}
                        </label>
                        <div className="w-full">
                          <EditDatePicker
                            value={incomeForm.job_end_date ?? ''}
                            onChange={(value) =>
                              handleOtherIncomeChange(index, "job_end_date", value)
                            }
                            allowPastDates={true}
                            allowFutureDates={false}
                            displayFormat="mdy"
                            disabled={incomeForm.present_job}
                            calendarPortalId="liberty-income-employment-datepicker"
                            className={
                              otherIncomeFieldMsg(index, "job_end_date")
                                ? editStyles.inputError
                                : ""
                            }
                          />
                          {otherIncomeFieldMsg(index, "job_end_date") ? (
                            <div className={editStyles.errorMessage} role="alert">
                              {otherIncomeFieldMsg(index, "job_end_date")}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="pb-2 text-gray-500 font-medium text-sm">OR</div>
                    </div>

                    <div className="col-span-12 md:col-span-2 flex items-start pt-[27px]">
                      <label className="inline-grid min-h-9 cursor-pointer grid-cols-[16px_auto] items-center gap-x-2" data-testid={`application-income-tab-other-income-${index}-currently-active-checkbox-label`}>
                        <input
                          type="checkbox"
                          checked={incomeForm.present_job}
                          data-testid={`application-income-tab-other-income-${index}-currently-active-checkbox`}
                onChange={(e) =>
                            handleOtherIncomeChange(
                              index,
                              "present_job",
                              e.target.checked,
                            )
                          }
                          className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-500"
                        />
                        <div className="flex flex-col items-start justify-center text-left">
                          <span className="text-sm font-medium leading-[15px] text-gray-700">Currently</span>
                          <span className="text-sm font-medium leading-[15px] text-gray-700">Active</span>
                        </div>
                      </label>
                    </div>

                    <div className="col-span-12 md:col-span-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-testid={`application-income-tab-other-income-${index}-months-of-income-label`}>
                        Months of this Income<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className={otherIncomeInputClass(
                          Boolean(otherIncomeFieldMsg(index, "job_duration")),
                          "w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-600 cursor-not-allowed",
                        )}
                        value={calculateJobDuration(
                          incomeForm.job_start_date ?? '',
                          incomeForm.present_job
                            ? undefined
                            : incomeForm.job_end_date,
                        )}
                        readOnly
                        placeholder="Duration"
                        data-testid={`application-income-tab-other-income-${index}-income-duration-input`}
                      />
                      {otherIncomeFieldMsg(index, "job_duration") ? (
                        <div className={editStyles.errorMessage} role="alert">
                          {otherIncomeFieldMsg(index, "job_duration")}
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}

          {/* Add Other Income Button - Styled as Link */}
          <button
            type="button"
            className="flex items-center text-blue-500 hover:text-blue-600 font-medium mt-4"
            data-testid="application-income-tab-add-other-income-button"
                onClick={handleAddOtherIncome}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
            Add Other Income
          </button>
        </div>

        <BankLinkingEditSection
          ninjaAccounts={ninjaAccounts}
          ninjaConfig={ninjaConfig}
          isBankLinkingLoading={isBankLinkingLoading}
          isOpeningNinjaBankLink={isOpeningNinjaBankLink}
          onLinkBank={handleLinkBank}
        />

        {showSupportingUploadsSection(applicationData) && (
        <div className={styles.incomeNestedSection} data-testid="application-income-tab-section-supporting-documents">
          <h3 className={styles.incomeSubsectionTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <Upload className="h-4 w-4" />
            </span>
            Supporting Documents <span className="text-xs font-semibold text-red-500">*required</span>
          </h3>
          <ul className={`${styles.incomeEditNote} list-inside list-disc`}>
            <li>Please upload any supporting documentation requested by Liberty Rent here.</li>
          </ul>

          <IncomeDocumentationSection
            isAwaiting={isAwaitingStatus}
            message={null}
            isReadOnly={false}
            omitOuterCard
            omitDescriptor
            showDropzone
            uploading={proofOfIncomeUploading}
            onUploadFiles={async (files) => handleIncomeDocumentFiles(files)}
            existingUploadCount={supportingUploadExistingCount}
            existingUploadNames={supportingUploadExistingNames}
            proofOfIncome={[]}
            onPreview={() => {}}
            onRemove={() => {}}
            hideFileList
          />

          {ninjaIncomeDocuments.length > 0 && (
            <div className="border border-gray-200 rounded-lg bg-white mt-4 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="text-[15px] font-bold text-[#232946]">
                  Income Verification Documents
                </h3>
              </div>
              <div className="flex flex-col">
                {ninjaIncomeDocuments.map((file, index) =>
                  renderEditSupportingFileRow(file, index, {
                    allFiles: incomeVerificationDocuments,
                  }),
                )}
              </div>
            </div>
          )}

          {proofIncomeDocuments.length > 0 && (
            <div className="border border-gray-200 rounded-lg bg-white mt-4 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="text-[15px] font-bold text-[#232946]">
                  Additional Supporting Documents
                </h3>
              </div>
              <div className="flex flex-col">
                {proofIncomeDocuments.map((file, index) =>
                  renderEditSupportingFileRow(file, index, {
                    allFiles: incomeVerificationDocuments,
                    allowRemove: isAwaitingStatus,
                  }),
                )}
              </div>
            </div>
          )}
        </div>
        )}

        </div>


        {showNinjaIframePopup && (
          <NinjaIframePopup
            applicantId={applicantId}
            isOpen={showNinjaIframePopup}
            ninjaVersion={ninjaConfig?.NINJA_VERSION}
            responseData={ninjaIbvResponseData}
            isLoading={isOpeningNinjaBankLink}
            onClose={() => {
              setShowNinjaIframePopup(false);
              setNinjaIbvResponseData(null);
            }}
            onSuccess={async (data) => {
              const publicToken = data.product_request_id || data.request_id;
              if (!publicToken) {
                showError("No product request ID received. Please try again.");
                return;
              }
              try {
                await linkNinjaAccount(applicantId, publicToken);
                await refreshAccounts();
                if (onRefresh) {
                  await onRefresh();
                }
                showSuccess("Account Linked Successfully");
                setShowNinjaIframePopup(false);
                setNinjaIbvResponseData(null);
              } catch (error: unknown) {
                showError(
                  error instanceof Error
                    ? error.message
                    : "Failed to link account. Please try again.",
                );
              }
            }}
          />
        )}

        {/* Job End Date popup — mirrors Angular JobEndDateComponent shown via
            dialogService.showdialog when user clicks "Make Previous Job". */}
        <JobEndDatePopup
          isOpen={pendingMakePreviousIndex !== null}
          value={pendingMakePreviousEndDate}
          onChange={setPendingMakePreviousEndDate}
          onCancel={() => {
            setPendingMakePreviousIndex(null);
            setPendingMakePreviousEndDate("");
          }}
          onSave={() => {
            if (pendingMakePreviousIndex === null) return;
            if (!pendingMakePreviousEndDate) return;
            const rowIndex = pendingMakePreviousIndex;
            const endDate = pendingMakePreviousEndDate;
            setPendingMakePreviousIndex(null);
            setPendingMakePreviousEndDate("");
            // Angular flow: showdialog -> makeJobConfirm. We chain a confirm dialog next.
            setMakePreviousConfirm({ rowIndex, endDate });
          }}
        />

        <ConfirmDialog
          isOpen={makePreviousConfirm !== null}
          title="Make Previous Job"
          message="Are you sure want to make this current job as Previous Job?"
          primaryLabel="Yes"
          secondaryLabel="No"
          overlayDataTestId="application-income-tab-confirm-make-previous-job"
          primaryButtonDataTestId="application-income-tab-confirm-make-previous-job-primary-button"
          secondaryButtonDataTestId="application-income-tab-confirm-make-previous-job-secondary-button"
          closeButtonDataTestId="application-income-tab-confirm-make-previous-job-close-button"
          onPrimary={() => {
            if (!makePreviousConfirm) return;
            void handleMakePrevious(
              makePreviousConfirm.rowIndex,
              makePreviousConfirm.endDate,
            );
            setMakePreviousConfirm(null);
          }}
          onSecondary={() => setMakePreviousConfirm(null)}
          onDismiss={() => setMakePreviousConfirm(null)}
        />

        <ConfirmDialog
          isOpen={makeCurrentConfirm !== null}
          title="Make Current Job"
          message="Are you sure want to make this previous job as Current Job?"
          primaryLabel="Yes"
          secondaryLabel="No"
          overlayDataTestId="application-income-tab-confirm-make-current-job"
          primaryButtonDataTestId="application-income-tab-confirm-make-current-job-primary-button"
          secondaryButtonDataTestId="application-income-tab-confirm-make-current-job-secondary-button"
          closeButtonDataTestId="application-income-tab-confirm-make-current-job-close-button"
          onPrimary={() => {
            if (!makeCurrentConfirm) return;
            void handleMakeCurrent(makeCurrentConfirm.rowIndex);
            setMakeCurrentConfirm(null);
          }}
          onSecondary={() => setMakeCurrentConfirm(null)}
          onDismiss={() => setMakeCurrentConfirm(null)}
        />
      </div>
  );
}
